import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Train, User, LogOut, Menu, X, ChevronDown, 
  LayoutDashboard, Settings, Crown
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '../components/ui/sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { useAuth } from '../lib/api';

export const Header = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = React.useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const navLinks = [
    { href: '/about', label: 'About Us' },
    { href: '/skills', label: 'Skills' },
    { href: '/tests', label: 'Tests' },
    { href: '/resources', label: 'Resources' },
    { href: '/dashboard', label: 'Dashboard' },
    { href: '/pricing', label: 'Pricing' },
    { href: '/faq', label: 'FAQ' },
    { href: '/contact', label: 'Contact' },
  ];

  return (
    <header className="glass-header sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="header-logo">
            <img 
              src="/TDF.png" 
              alt="TDF Logo" 
              className="w-10 h-10 rounded-sm object-cover"
            />
            <div className="flex flex-col">
              <span className="font-heading text-xl font-bold text-[#0B1D51] tracking-tight leading-tight">
                TDF Psychometrics
              </span>
              <span className="text-xs text-[#64748B] leading-tight">
                by Train Driver Foundation
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-4 xl:gap-6">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className="text-sm font-medium text-[#64748B] hover:text-[#0B1D51] transition-colors whitespace-nowrap"
                data-testid={`nav-${link.label.toLowerCase()}`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Auth Section */}
          <div className="hidden lg:flex items-center gap-3 xl:gap-4">
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    className="flex items-center gap-2"
                    data-testid="user-menu-trigger"
                  >
                    <div className="w-8 h-8 bg-[#0B1D51] rounded-full flex items-center justify-center">
                      <User className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-sm font-medium">{user.name}</span>
                    {user.is_member && (
                      <Crown className="w-4 h-4 text-[#FF9F1C]" />
                    )}
                    <ChevronDown className="w-4 h-4 text-[#64748B]" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem asChild>
                    <Link to="/dashboard" className="flex items-center gap-2" data-testid="menu-dashboard">
                      <LayoutDashboard className="w-4 h-4" />
                      Dashboard
                    </Link>
                  </DropdownMenuItem>
                  {user.is_admin && (
                    <DropdownMenuItem asChild>
                      <Link to="/admin" className="flex items-center gap-2" data-testid="menu-admin">
                        <Settings className="w-4 h-4" />
                        Admin Panel
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={handleLogout}
                    className="flex items-center gap-2 text-red-600"
                    data-testid="menu-logout"
                  >
                    <LogOut className="w-4 h-4" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" data-testid="login-btn">
                    Sign In
                  </Button>
                </Link>
                <Link to="/register">
                  <Button className="bg-[#0B1D51] hover:bg-[#0B1D51]/90" data-testid="register-btn">
                    Get Started
                  </Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu */}
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <Button variant="ghost" size="icon" data-testid="mobile-menu-btn">
                {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[280px]">
              <div className="flex flex-col gap-6 mt-6">
                {navLinks.map((link) => (
                  <Link
                    key={link.href}
                    to={link.href}
                    onClick={() => setMobileOpen(false)}
                    className="text-lg font-medium text-[#0F172A] hover:text-[#0B1D51]"
                  >
                    {link.label}
                  </Link>
                ))}
                <div className="border-t pt-6">
                  {user ? (
                    <div className="flex flex-col gap-4">
                      <div className="flex items-center gap-2">
                        <div className="w-10 h-10 bg-[#0B1D51] rounded-full flex items-center justify-center">
                          <User className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <p className="font-medium">{user.name}</p>
                          <p className="text-sm text-[#64748B]">{user.email}</p>
                        </div>
                      </div>
                      <Link
                        to="/dashboard"
                        onClick={() => setMobileOpen(false)}
                        className="text-lg font-medium"
                      >
                        Dashboard
                      </Link>
                      {user.is_admin && (
                        <Link
                          to="/admin"
                          onClick={() => setMobileOpen(false)}
                          className="text-lg font-medium"
                        >
                          Admin Panel
                        </Link>
                      )}
                      <Button
                        variant="outline"
                        onClick={() => {
                          handleLogout();
                          setMobileOpen(false);
                        }}
                        className="justify-start text-red-600"
                      >
                        <LogOut className="w-4 h-4 mr-2" />
                        Logout
                      </Button>
                    </div>
                  ) : (
                    <div className="flex flex-col gap-3">
                      <Link to="/login" onClick={() => setMobileOpen(false)}>
                        <Button variant="outline" className="w-full">
                          Sign In
                        </Button>
                      </Link>
                      <Link to="/register" onClick={() => setMobileOpen(false)}>
                        <Button className="w-full bg-[#0B1D51]">
                          Get Started
                        </Button>
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export const Footer = () => {
  return (
    <footer className="bg-[#0B1D51] text-white py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          <div className="col-span-1 md:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <img 
                src="/TDF.png" 
                alt="TDF Logo" 
                className="w-12 h-12 rounded-sm object-cover bg-white p-1"
              />
              <div className="flex flex-col">
                <span className="font-heading text-xl font-bold tracking-tight leading-tight">
                  TDF Psychometrics
                </span>
                <span className="text-xs text-white/60 leading-tight">
                  by Train Driver Foundation
                </span>
              </div>
            </div>
            <p className="text-white/70 max-w-sm mb-4">
              Official psychometric test preparation from the Train Driver Foundation. 
              Practice all essential assessment areas used in UK train driver recruitment.
            </p>
            <a 
              href="https://www.traindriverfoundation.com/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm text-[#FF9F1C] hover:text-[#FFB84D] transition-colors font-medium"
            >
              Visit traindriverfoundation.com →
            </a>
          </div>
          <div>
            <h4 className="font-heading text-lg font-semibold mb-4">Quick Links</h4>
            <ul className="space-y-2">
              <li>
                <Link to="/tests" className="text-white/70 hover:text-white transition-colors">
                  All Tests
                </Link>
              </li>
              <li>
                <Link to="/resources" className="text-white/70 hover:text-white transition-colors">
                  Resources & Guides
                </Link>
              </li>
              <li>
                <Link to="/pricing" className="text-white/70 hover:text-white transition-colors">
                  Pricing
                </Link>
              </li>
              <li>
                <Link to="/dashboard" className="text-white/70 hover:text-white transition-colors">
                  Dashboard
                </Link>
              </li>
              <li>
                <Link to="/about" className="text-white/70 hover:text-white transition-colors">
                  About Us
                </Link>
              </li>
              <li>
                <Link to="/skills" className="text-white/70 hover:text-white transition-colors">
                  Driver Skills
                </Link>
              </li>
              <li>
                <Link to="/faq" className="text-white/70 hover:text-white transition-colors">
                  FAQ
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-white/70 hover:text-white transition-colors">
                  Contact
                </Link>
              </li>
              <li>
                <a 
                  href="https://www.traindriverfoundation.com/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-white/70 hover:text-white transition-colors"
                >
                  Train Driver Foundation
                </a>
              </li>
            </ul>
          </div>
          <div>
            <h4 className="font-heading text-lg font-semibold mb-4">Tests</h4>
            <ul className="space-y-2">
              <li>
                <Link to="/tests/attention-selective" className="text-white/70 hover:text-white transition-colors">
                  Selective Attention
                </Link>
              </li>
              <li>
                <Link to="/tests/attention-divided" className="text-white/70 hover:text-white transition-colors">
                  Divided Attention
                </Link>
              </li>
              <li>
                <Link to="/tests/vigilance" className="text-white/70 hover:text-white transition-colors">
                  Vigilance
                </Link>
              </li>
              <li>
                <Link to="/tests/perception" className="text-white/70 hover:text-white transition-colors">
                  Perception
                </Link>
              </li>
              <li>
                <Link to="/tests/memory" className="text-white/70 hover:text-white transition-colors">
                  Memory Sequences
                </Link>
              </li>
              <li>
                <Link to="/tests/reaction-time" className="text-white/70 hover:text-white transition-colors">
                  Reaction Time
                </Link>
              </li>
              <li>
                <Link to="/tests/hand-coordination" className="text-white/70 hover:text-white transition-colors">
                  Hand Coordination
                </Link>
              </li>
              <li>
                <Link to="/tests/reasoning" className="text-white/70 hover:text-white transition-colors">
                  Reasoning
                </Link>
              </li>
              <li>
                <Link to="/tests/behavioural" className="text-white/70 hover:text-white transition-colors">
                  Situational Judgement
                </Link>
              </li>
              <li>
                <Link to="/tests/rule-based-prioritising" className="text-white/70 hover:text-white transition-colors">
                  Rule-Based Prioritising
                </Link>
              </li>
              <li>
                <Link to="/tests/multitasking-workload" className="text-white/70 hover:text-white transition-colors">
                  Multitasking & Workload
                </Link>
              </li>
              <li>
                <Link to="/tests/sequencing" className="text-white/70 hover:text-white transition-colors">
                  Task Sequencing (TRP2)
                </Link>
              </li>
              <li>
                <Link to="/tests/rules-acquisition" className="text-white/70 hover:text-white transition-colors">
                  Rules Acquisition (RAAT)
                </Link>
              </li>
              <li>
                <Link to="/tests/communication-accuracy" className="text-white/70 hover:text-white transition-colors">
                  Communication Accuracy
                </Link>
              </li>
              <li>
                <Link to="/tests/detail-checking" className="text-white/70 hover:text-white transition-colors">
                  Detail Checking
                </Link>
              </li>
              <li>
                <Link to="/tests/northern-online-screening" className="text-white/70 hover:text-white transition-colors flex items-center gap-1">
                  Northern style Maki test
                  <span className="text-[10px] bg-[#FF9F1C] text-white px-1 py-0.5 rounded">NEW</span>
                </Link>
              </li>
            </ul>
          </div>
        </div>
        <div className="border-t border-white/10 mt-8 pt-8 text-center text-white/50 text-sm">
          <p>&copy; {new Date().getFullYear()} Train Driver Foundation. All rights reserved.</p>
          <p className="mt-1 text-xs">TDF Psychometrics is an official TDF resource for aspiring train drivers.</p>
        </div>
      </div>
    </footer>
  );
};

export const PageLayout = ({ children, showFooter = true }) => {
  return (
    <div className="min-h-screen flex flex-col overflow-x-hidden">
      <Header />
      <main className="flex-1">
        {children}
      </main>
      {showFooter && <Footer />}
    </div>
  );
};
